// Where media lives, and how it gets into memory. The one module every surface
// shares, and deliberately the only one: it holds no DOM, no gsap, no Games and
// no Event, so importing it costs a page nothing but these forty lines.
//
// It exists because Beacon needed the preload gate and nothing else.
// `presentation.js` builds two <audio> elements into the body and fetches
// thirty TV-only clips the moment it is imported, so a second surface could not
// borrow the gate without also downloading the television. The half that is
// genuinely common — the key builder, the blob cache, and the fetch loop — is
// here; everything the loader screen does about it stays where it was.

// The Pack's key prefix. `VITE_MEDIA_BASE` is the only thing that differs
// between a party on this desk and one on a customer's TV; unset, every path is
// a local one `server.js` already serves by shape, which is why `npm start`
// against source works with no build and no bucket.
export const base = import.meta.env?.VITE_MEDIA_BASE ?? "/media";

// `packs/<pack>/r<revision>/…` — the R2 key and the shape on disk both, so
// publishing is a sync and nothing is rewritten in transit. The revision is a
// path segment rather than a `?v=` query because these objects are immutable
// and cached for a year: re-cutting bumps the revision and writes new keys, and
// a query string on a year-long `immutable` response is the thing a CDN is
// entitled to ignore.
//
// Encoded per segment, because an id is a path segment and a Pack that grows
// one with a space in it should still resolve.
export const at =
  (pack, version) =>
  (...parts) =>
    `${base}/packs/${pack}/r${version}/${parts.map(encodeURIComponent).join("/")}`;

// Preloaded assets are held as blob URLs, so a running show needs no network.
const cache = new Map();
// The empty resource, for an asset the gate has not cached yet.
//
// Handing a caller the remote URL instead is what poisoned the browser cache:
// an <img>, <audio> or background-image built before the gate has run fetches
// that URL as an element load, which carries no `Origin` header — so R2's
// scoped policy answers without `access-control-allow-origin`, and Pack media
// is served `immutable, max-age=31536000`. The headerless copy is then the one
// the *preload* `fetch()` gets handed for the next year, and refused, and the
// show will not start. A blocked boot is where this happened: the gate returns
// before caching anything, and `voices` and `announcements` still build their
// elements. Every media path that already checks `ready` was avoiding this
// without knowing it; this is the same rule for the two that did not, and for
// whatever is written next.
//
// `data:,` rather than "": every consumer treats it as a failed load, and none
// of them go to the network for it — an empty url() in a background-image asks
// for the document.
const NOTHING = "data:,";
let ready = false;
export const assetsReady = () => ready;
export const src = (url) => cache.get(url) ?? (ready ? url : NOTHING);

// Fetch every asset into memory, reporting `(fraction, done, total)` as they
// land. Throws on the first one that cannot be had — a surface with a missing
// asset must not start, and the caller says so in its own words.
//
// An empty list is a legitimate call and means "there is nothing to load":
// it marks the surface ready and resolves. That is how a headless import and a
// Pack with no media both get past the gate.
export async function fetchAll(urls, onProgress = () => {}) {
  let done = 0;
  await Promise.all(
    urls.map(async (url) => {
      // Name the asset, not the transport: "Failed to fetch" tells a host nothing.
      //
      // The retry is for one specific poisoning, and it is worth a line because
      // the failure is a show that will not start. Pack media is served
      // `immutable, max-age=31536000`, so a copy the browser stored from a
      // request that carried no `Origin` — an earlier visit, another surface's
      // plain <img> — has no allow-origin header and is reused here for a year.
      // The CORS refusal that follows is indistinguishable from a dead network.
      // `cache: "reload"` goes past the stored copy and takes the answer R2
      // gives a real cross-origin request.
      const get = (options) => fetch(url, options).catch(() => null);
      const response = (await get()) ?? (await get({ cache: "reload" }));
      if (!response?.ok) throw new Error(`${url} is unavailable`);
      cache.set(url, URL.createObjectURL(await response.blob()));
      onProgress(++done / urls.length, done, urls.length);
    }),
  );
  ready = true;
  return true;
}
